import React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import TableSortLabel from '@mui/material/TableSortLabel';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label='first page'>
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const TableWithPagination = (props) => {
  const { headers, objects, keys, formatting, perPage = 10, isMobile = false } = props;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(perPage);
  const [orderBy, setOrderBy] = React.useState(null);
  const [order, setOrder] = React.useState('asc');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedObjects = React.useMemo(() => {
    if (!orderBy) return objects;

    return [...objects].sort((a, b) => {
      const aValue = formatting && formatting[orderBy] ? formatting[orderBy](a[orderBy]) : a[orderBy];
      const bValue = formatting && formatting[orderBy] ? formatting[orderBy](b[orderBy]) : b[orderBy];

      // Handle the new formatting structure that includes display and sortValue
      const aSortValue = aValue?.sortValue !== undefined ? aValue.sortValue : aValue;
      const bSortValue = bValue?.sortValue !== undefined ? bValue.sortValue : bValue;

      // Check if values are numeric
      const aNum = Number(aSortValue);
      const bNum = Number(bSortValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum);

      if (isNumeric) {
        if (order === 'asc') {
          return aNum - bNum;
        } else {
          return bNum - aNum;
        }
      } else {
        // String comparison
        const aStr = String(aSortValue).toLowerCase();
        const bStr = String(bSortValue).toLowerCase();
        if (order === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      }
    });
  }, [objects, orderBy, order, formatting]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * perPage - objects.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <TableContainer component={Paper} sx={{ marginTop: 1, marginBottom: 12.5, minWidth: isMobile ? '' : 725 }}>
      <Table sx={{ minWidth: 500 }} aria-label='table with pagination'>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell key={index} sortDirection={orderBy === keys[index] ? order : false}>
                <TableSortLabel
                  active={orderBy === keys[index]}
                  direction={orderBy === keys[index] ? order : 'asc'}
                  onClick={() => handleRequestSort(keys[index])}
                  sx={{ fontFamily: 'HK Grotesk', fontWeight: 600, fontSize: isMobile ? 12 : 14 }}
                >
                  {header}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? sortedObjects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : sortedObjects
          ).map((row, index) => (
            <TableRow key={index}>
              {keys.map((key, index) => {
                const formattedValue = formatting && formatting[key] ? formatting[key](row[key]) : row[key];
                return (
                  <TableCell
                    key={index}
                    component='th'
                    scope='row'
                    sx={{ fontFamily: 'HK Grotesk', fontSize: isMobile ? 12 : 14 }}
                  >
                    {formattedValue?.display || formattedValue}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={headers.length} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={headers.length}
              count={objects.length}
              rowsPerPage={rowsPerPage}
              page={page}
              rowsPerPageOptions={[10, 20, 50, 100]}
              slotProps={{
                select: {
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                },
              }}
              sx={{
                '.css-pdct74-MuiTablePagination-selectLabel': {
                  fontFamily: 'HK Grotesk',
                  fontSize: isMobile ? 12 : 14,
                },
                '.css-m3l1ig-MuiNativeSelect-select-MuiInputBase-input': {
                  fontFamily: 'HK Grotesk',
                  fontSize: isMobile ? 12 : 14,
                },
                '.css-levciy-MuiTablePagination-displayedRows': {
                  fontFamily: 'HK Grotesk',
                  fontSize: isMobile ? 12 : 14,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default TableWithPagination;
